import {
  alertMessages,
  monitoringMessages,
  resolveConfig,
  privacyNotice,
  visionMessages,
} from "@studystraight/shared-config";
import type { SessionRepository } from "@studystraight/session-core";
import type { PostureBaseline, StudySession, VisionResult } from "@studystraight/shared-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { toSessionOptions, type AppSettings } from "../../app/settings.js";
import { useCamera } from "../../hooks/useCamera.js";
import { useMonitoringSnapshot } from "../../hooks/useMonitoringSnapshot.js";
import { useVisionTracking } from "../../hooks/useVisionTracking.js";
import { useWakeLock } from "../../hooks/useWakeLock.js";
import { WebAlertProvider } from "../../platform/alert/WebAlertProvider.js";
import { CameraPreview } from "../../ui/CameraPreview.js";
import { VisionFailureBanner } from "../../ui/VisionFailureBanner.js";
import { DebugOverlay } from "../debug/DebugOverlay.js";
import { SkeletonCanvas } from "../debug/SkeletonCanvas.js";
import { useDebugRecorder } from "../debug/useDebugRecorder.js";
import { createMonitoringController } from "./MonitoringController.js";
import { PostureGauge } from "./PostureGauge.js";

/**
 * Nhịp "không có frame" (§16 tracking lost).
 *
 * 500ms là đủ nhanh so với `trackingLostAfterMs = 2000` mà không tự tạo thêm việc: nhịp này chỉ
 * chạy máy trạng thái, không chạy inference.
 */
const TICK_MS = 500;

export interface MonitoringScreenProps {
  baseline: PostureBaseline;
  settings: AppSettings;
  repository: SessionRepository;
  /** Storage đã lỗi ít nhất một lần (§27) — hiện cảnh báo một lần, không chặn phiên học. */
  storageFailed: boolean;
  onSettingsChange: (settings: AppSettings) => void;
  onFinish: (session: StudySession) => void;
  onRecalibrate: () => void;
}

/** §24 — màn hình học: camera trên, điểm lớn ở giữa, thời gian và số lần nhắc ở dưới. */
export function MonitoringScreen({
  baseline,
  settings,
  repository,
  storageFailed,
  onSettingsChange,
  onFinish,
  onRecalibrate,
}: MonitoringScreenProps): React.JSX.Element {
  const camera = useCamera({ facingMode: settings.facingMode });
  const cameraLive = camera.status === "live";
  const recorder = useDebugRecorder();
  // `debugMode` là quyền, `debugPanelOpen` là trạng thái — cần cả hai để panel không mắc kẹt ở
  // trạng thái mở sau khi người dùng tắt Debug Mode (lúc đó nút bật/tắt không còn hiện).
  const debug = settings.debugMode && settings.debugPanelOpen;
  const [voiceFallback, setVoiceFallback] = useState(false);
  const [ending, setEnding] = useState(false);

  // Dependency là `settings.overrides` chứ không phải `settings`: mỗi lần người dùng bật/tắt giọng
  // nói, App tạo một object settings mới — nếu `config` đổi theo thì `controller` bên dưới bị dựng
  // lại và phiên học đang chạy bị reset về 0 giây.
  const config = useMemo(
    () => resolveConfig(baseline.placement, settings.overrides[baseline.placement]),
    [baseline.placement, settings.overrides],
  );

  /**
   * Controller sống theo `baseline` — đổi baseline nghĩa là một phiên học khác.
   *
   * `settings` **không** nằm trong dependency: dựng lại controller giữa phiên sẽ xoá sạch số liệu
   * đang gom. Những gì đổi được giữa phiên (voice on/off) đi qua setter ở effect bên dưới.
   */
  const controller = useMemo(
    () =>
      createMonitoringController({
        baseline,
        config,
        repository,
        provider: new WebAlertProvider({ onTextFallback: () => setVoiceFallback(true) }),
      }),
    [baseline, config, repository],
  );

  const snapshot = useMonitoringSnapshot(controller);

  const onFrame = useCallback(
    (vision: VisionResult, inferenceMs: number) => {
      // Ring buffer nhận mọi frame kể cả khi panel debug đóng (§25) — xem SetupScreen.
      recorder.push(vision, inferenceMs);
      controller.pushFrame(vision);
    },
    [controller, recorder],
  );

  const vision = useVisionTracking({
    placement: baseline.placement,
    videoRef: camera.videoRef,
    enabled: cameraLive,
    onFrame,
  });

  const startCamera = camera.start;
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  // Options được đọc BÊN TRONG effect và effect chỉ phụ thuộc `controller`: `settings` và
  // `baseline` đã đóng băng cho phiên này (xem `SessionSetup` trong navigation.ts), nên đưa chúng
  // vào dependency chỉ tạo nguy cơ start lại phiên giữa lúc trẻ đang học.
  const optionsRef = useRef({ settings, placement: baseline.placement });
  optionsRef.current = { settings, placement: baseline.placement };
  useEffect(() => {
    const { settings: current, placement } = optionsRef.current;
    void controller.startSession(toSessionOptions(current, placement));
  }, [controller]);

  useEffect(() => {
    controller.setMuted(!settings.voiceEnabled);
  }, [controller, settings.voiceEnabled]);

  useEffect(() => {
    if (storageFailed) controller.reportStorageFailure();
  }, [controller, storageFailed]);

  useEffect(() => {
    const id = setInterval(() => controller.tick(), TICK_MS);
    return () => clearInterval(id);
  }, [controller]);

  useWakeLock(snapshot.running);

  const finish = useCallback(
    (after: (session: StudySession) => void) => {
      setEnding(true);
      camera.stop();
      controller
        .endSession()
        .then(after)
        .catch(() => setEnding(false));
    },
    [camera, controller],
  );

  return (
    <main className="screen">
      <header className="topbar">
        <h2 className="topbar__title">{monitoringMessages.title}</h2>
        {settings.debugMode ? (
          <button
            type="button"
            className="btn btn--ghost topbar__end"
            aria-pressed={debug}
            onClick={() => onSettingsChange({ ...settings, debugPanelOpen: !debug })}
          >
            {debug ? "Ẩn debug" : "Debug"}
          </button>
        ) : null}
      </header>

      {/* Bật debug = chia hai cột (preview + điểm bên trái, HUD bên phải) trên màn đủ rộng. Xếp
          dọc thì HUD 17 hàng đẩy khung camera xuống còn vài chục pixel — đúng lúc cần nhìn skeleton
          nhất thì lại không thấy gì (§25). */}
      <div className={debug ? "monitor monitor--debug" : "monitor"}>
        <div className="monitor__main">
          {/* Preview nhỏ hơn ở Setup: trong lúc học, điểm số mới là thứ trẻ cần thấy (§24). */}
          <div className="stage stage--compact">
            <CameraPreview camera={camera}>
              {debug ? (
                <SkeletonCanvas
                  getVision={recorder.latestVision}
                  minVisibility={config.posture.minLandmarkVisibility}
                  mirrored={camera.facingMode === "user"}
                />
              ) : null}
            </CameraPreview>
          </div>

          <PostureGauge
            score={snapshot.score}
            status={snapshot.status}
            statusLabel={snapshot.statusLabel}
            unknown={snapshot.trackingLost || snapshot.paused}
          />

          <p className="monitor__meta">
            <span>{snapshot.elapsedLabel}</span>
            {snapshot.remainingLabel === null ? null : (
              <span className="muted">
                {monitoringMessages.goal} {snapshot.remainingLabel}
              </span>
            )}
            <span>
              {monitoringMessages.alerts} {snapshot.alertCount}
            </span>
          </p>

          {/* Vision chết thì mọi thông báo khác đều vô nghĩa (điểm số đứng, "không thấy con" gây
              hiểu nhầm là do ngồi lệch) — báo trước, và cho đường dựng lại engine ngay tại đây để
              không phải bỏ phiên học đang chạy. */}
          <VisionFailureBanner failure={vision.error} onRetry={vision.retry} showDetail={debug} />

          {/* Băng chữ: luôn hiện câu nhắc gần nhất, kể cả khi có giọng nói — trẻ có thể vừa bỏ tai
              nghe ra, và §20 yêu cầu nhắc nhẹ nhàng chứ không chỉ bằng âm thanh. */}
          {snapshot.eyeBreakDue ? (
            <div className="banner" role="status">
              <span>{alertMessages.EYE_BREAK}</span>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => controller.dismissEyeBreak()}
              >
                {monitoringMessages.eyeBreakDone}
              </button>
            </div>
          ) : snapshot.paused ? (
            <div className="banner" role="status">
              <span>{monitoringMessages.paused}</span>
            </div>
          ) : snapshot.trackingLost && !vision.error ? (
            <p className="hint">{monitoringMessages.trackingLost}</p>
          ) : snapshot.lastMessage ? (
            <p className="monitor__message">{snapshot.lastMessage}</p>
          ) : vision.ready || vision.error ? null : (
            <p className="hint">{visionMessages.loading}</p>
          )}

          {snapshot.goalReached ? <p className="hint">{monitoringMessages.goalReached}</p> : null}
          {voiceFallback ? <p className="hint">{monitoringMessages.voiceMissing}</p> : null}
          {snapshot.storageFailed ? (
            <p className="hint">{monitoringMessages.storageFailed}</p>
          ) : null}

          <div className="actions">
            <button
              type="button"
              className="btn btn--secondary"
              disabled={ending}
              onClick={() => (snapshot.paused ? controller.resume() : controller.pause())}
            >
              {snapshot.paused ? monitoringMessages.resume : monitoringMessages.pause}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={ending}
              onClick={() => finish(onFinish)}
            >
              {ending ? monitoringMessages.ending : monitoringMessages.end}
            </button>
          </div>

          {/* Hiệu chỉnh lại giữa phiên = kết thúc phiên hiện tại (baseline mới thì số liệu cũ không
              còn so được), nên nó phải lưu phiên chứ không âm thầm bỏ. */}
          <button
            type="button"
            className="btn btn--ghost"
            disabled={ending}
            onClick={() => finish(() => onRecalibrate())}
          >
            {monitoringMessages.recalibrate}
          </button>

          {/* Debug bật thì privacy notice nhường chỗ cho HUD — nó đã hiện ở Home/Setup/Settings. */}
          {debug ? null : <p className="privacy">{privacyNotice}</p>}
        </div>

        {debug ? (
          <div className="monitor__side">
            <DebugOverlay
              recorder={recorder}
              perf={vision.perf}
              quality={vision.quality}
              config={config}
              engine={vision.capabilities}
            />
          </div>
        ) : null}
      </div>
    </main>
  );
}
